import type * as React from 'react';
import {
  Bank as BankIcon,
  Buildings as BuildingsIcon,
  CalendarCheck as CalendarCheckIcon,
  CalendarPlus as CalendarPlusIcon,
  FileArrowUp as FileArrowUpIcon,
  FileText as FileTextIcon,
  GearSix as GearSixIcon,
  HouseLine as HouseLineIcon,
  MegaphoneSimple as MegaphoneSimpleIcon,
  PaperPlaneTilt as PaperPlaneTiltIcon,
  ShieldCheck as ShieldCheckIcon,
  Sliders as SlidersIcon,
  Tag as TagIcon,
  ThumbsUp as ThumbsUpIcon,
  Trash as TrashIcon,
  UserMinus as UserMinusIcon,
  UserPlus as UserPlusIcon,
  UsersThree as UsersThreeIcon,
} from '@phosphor-icons/react/dist/ssr';

/**
 * Who is allowed to see a how-to article. Mirrors the gating used on the
 * Help page:
 *  - everyone           → all signed-in users
 *  - board              → board members, community managers, account admins
 *  - community-manager  → community managers and account admins
 *  - account            → account-level admins only
 */
export type HelpAudience = 'everyone' | 'board' | 'community-manager' | 'account';

export interface HelpCategoryMeta {
  id: string;
  title: string;
  description: string;
}

export const HELP_CATEGORIES: HelpCategoryMeta[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    description: 'The basics every resident and manager should know first.',
  },
  {
    id: 'residents-units',
    title: 'Residents & units',
    description: 'Add people, create units, and manage roles and assignments.',
  },
  {
    id: 'reservations',
    title: 'Amenities & reservations',
    description: 'Book amenities and review approvals and deposits.',
  },
  {
    id: 'communication',
    title: 'Communication',
    description: 'Announcements, requests, and staying in touch.',
  },
  {
    id: 'documents-meetings',
    title: 'Documents & meetings',
    description: 'Share files and run board meetings.',
  },
  {
    id: 'settings-payments',
    title: 'Settings & payments',
    description: 'Configure your community and connect payouts.',
  },
];

export interface HelpStep {
  /** Short imperative title, e.g. "Open the Users page". */
  title: string;
  /** Optional longer explanation of what to do and what to expect. */
  detail?: string;
  /** Optional callout shown as a tip beneath the step. */
  tip?: string;
}

export interface HelpArticle {
  slug: string;
  title: string;
  summary: string;
  categoryId: string;
  audience: HelpAudience;
  icon: React.ElementType;
  /** Extra search terms beyond the title/summary. */
  keywords: string[];
  /** Where in the app this task starts, e.g. "Management → Users". */
  whereToStart?: string;
  /** Roughly how long the task takes, shown as a chip. */
  estimatedMinutes?: number;
  steps: HelpStep[];
  /** Slugs of related articles to suggest at the bottom. */
  related?: string[];
  /** Hide unless the matching community feature is enabled. */
  feature?: 'discussion';
}

export const HELP_ARTICLES: HelpArticle[] = [
  {
    slug: 'add-a-user',
    title: 'Add a user (resident or staff)',
    summary: 'Invite a new person to your community and optionally assign them to a unit.',
    categoryId: 'residents-units',
    audience: 'community-manager',
    icon: UserPlusIcon,
    keywords: ['invite', 'resident', 'member', 'new user', 'add person', 'welcome email', 'onboard'],
    whereToStart: 'Management → Users',
    estimatedMinutes: 3,
    steps: [
      {
        title: 'Open the Users page',
        detail: 'In the left sidebar, under the Management section, click Users.',
        tip: "Don't see Management or Users? You need a Community Manager (or account admin) role. Ask your administrator.",
      },
      {
        title: 'Click "Add User"',
        detail: 'The button is in the top-right of the Users page. A dialog titled "Add User" opens.',
      },
      {
        title: 'Enter the person\'s details',
        detail:
          'Fill in First Name, Last Name, and Email Address (all required). Add a Phone Number if you have it.',
        tip: 'If the email already belongs to an existing HOAONE user, they will simply be added to this community instead of creating a duplicate.',
      },
      {
        title: 'Choose a Community Role',
        detail:
          'Pick Community Member for a regular resident, Board Member for board access, or Community Manager for full management access.',
      },
      {
        title: 'Assign them to a unit (if applicable)',
        detail:
          'Use "Assign to Unit" to link the person to their home, then set the Unit Role (Unit Owner, Unit Tenant, or Unit Resident). A unit is required for Community Members and optional for board members and managers.',
        tip: "No unit exists yet? Create it first — see \"Add a unit\" — then come back and assign it.",
      },
      {
        title: 'Send the welcome email',
        detail:
          'Leave "Send welcome email" checked so the new user receives an invitation to set their password and sign in. Uncheck it only if you plan to invite them later.',
      },
      {
        title: 'Save',
        detail:
          'Click "Add User". You\'ll see a confirmation toast and the new person will appear in the Users list.',
      },
    ],
    related: ['add-a-unit', 'assign-user-to-unit'],
  },
  {
    slug: 'add-a-unit',
    title: 'Add a unit',
    summary: 'Create a property record so residents can be assigned to a home.',
    categoryId: 'residents-units',
    audience: 'community-manager',
    icon: HouseLineIcon,
    keywords: ['unit', 'property', 'address', 'home', 'create unit', 'rental'],
    whereToStart: 'Management → Units',
    estimatedMinutes: 2,
    steps: [
      {
        title: 'Open the Units page',
        detail: 'In the left sidebar, under Management, click Units.',
      },
      {
        title: 'Click "Add Unit"',
        detail: 'A dialog titled "Create Unit" opens.',
      },
      {
        title: 'Fill in the unit details',
        detail:
          'Enter the Address, Zip Code, City, and State (required). Add Address 2, Unit Number, and Floor if relevant.',
      },
      {
        title: 'Mark it as a rental if needed',
        detail: 'Turn on the "Rental Unit" toggle if this unit is rented out.',
      },
      {
        title: 'Create the unit',
        detail: 'Click "Create Unit". It will appear in the Units list, ready for resident assignment.',
      },
    ],
    related: ['add-a-user', 'assign-user-to-unit'],
  },
  {
    slug: 'assign-user-to-unit',
    title: 'Assign a user to a unit or change their role',
    summary: 'Link an existing resident to their home and set owner/tenant/manager roles.',
    categoryId: 'residents-units',
    audience: 'community-manager',
    icon: UsersThreeIcon,
    keywords: ['assign', 'unit', 'role', 'owner', 'tenant', 'resident', 'change role', 'remove from unit'],
    whereToStart: 'Management → Users → (select a user)',
    estimatedMinutes: 2,
    steps: [
      {
        title: 'Open the user',
        detail: 'Go to Management → Users and click the person\'s row to open their detail page.',
      },
      {
        title: 'Add them to a unit',
        detail:
          'In the Units section, click "Add to Unit". In the "Assign Unit" dialog, search for and select a unit.',
      },
      {
        title: 'Pick a unit role',
        detail: 'Choose Unit Owner, Unit Tenant, or Unit Resident, then click "Assign Unit".',
      },
      {
        title: 'Change a community role (optional)',
        detail:
          'In the "Community & Roles" section, use the Community Role dropdown to switch between Community Manager, Board Member, and Community Member. The change saves automatically.',
      },
      {
        title: 'Remove from a unit (optional)',
        detail:
          'On a unit card, click "Remove from Unit" and confirm. This only unlinks the unit — it does not delete the user.',
      },
    ],
    related: ['add-a-user', 'add-a-unit'],
  },
  {
    slug: 'create-a-reservation',
    title: 'Reserve an amenity',
    summary: 'Book a community amenity and pay a deposit if one is required.',
    categoryId: 'reservations',
    audience: 'everyone',
    icon: CalendarCheckIcon,
    keywords: ['reserve', 'booking', 'amenity', 'clubhouse', 'pool', 'deposit', 'book'],
    whereToStart: 'Reservations',
    estimatedMinutes: 3,
    steps: [
      {
        title: 'Open Reservations',
        detail: 'Click Reservations in the left sidebar.',
      },
      {
        title: 'Start a new reservation',
        detail:
          'Click "Start Reservation". (You can also click "Reserve this amenity" on a card in the Availability Calendar.)',
      },
      {
        title: 'Select the amenity',
        detail: 'Choose the amenity you want to book from the dropdown.',
      },
      {
        title: 'Pick a date and time',
        detail: 'Set the Date, Start Time, and End Time. Some amenities use fixed time blocks.',
      },
      {
        title: 'Add the details',
        detail:
          'Describe the Purpose of Reservation and any Special Requests. Answer extra questions (like whether alcohol will be served) and upload any required documents if prompted.',
      },
      {
        title: 'Submit (and pay a deposit if required)',
        detail:
          'Click "Create Reservation". If the amenity requires a deposit, the button reads "Reserve & Pay Deposit" and you\'ll confirm payment with a saved card.',
        tip: 'Reservations that require approval will show as pending until a manager approves them.',
      },
    ],
    related: ['approve-a-reservation'],
  },
  {
    slug: 'approve-a-reservation',
    title: 'Approve or reject a reservation',
    summary: 'Review pending amenity reservations and confirm or decline them.',
    categoryId: 'reservations',
    audience: 'board',
    icon: ThumbsUpIcon,
    keywords: ['approve', 'reject', 'pending', 'reservation', 'manager', 'deposit'],
    whereToStart: 'Settings → Community Settings → Reservations',
    estimatedMinutes: 2,
    steps: [
      {
        title: 'Open community reservations',
        detail: 'Go to Settings → Community Settings, then click Reservations in the sub-navigation.',
      },
      {
        title: 'Go to "Pending Approvals"',
        detail: 'Select the Pending Approvals tab to see reservations awaiting a decision.',
      },
      {
        title: 'Review the request',
        detail: 'Each card shows the amenity, requester, date/time, and any details. Click Edit first if you need to adjust the time.',
      },
      {
        title: 'Approve or reject',
        detail:
          'Click Approve, then confirm with "Approve Reservation" — the resident is notified it\'s confirmed. To decline, click Reject, add a Reason for Rejection, and confirm.',
      },
    ],
    related: ['create-a-reservation'],
  },
  {
    slug: 'create-an-announcement',
    title: 'Post an announcement',
    summary: 'Send a notice to your community, optionally by email and on a schedule.',
    categoryId: 'communication',
    audience: 'board',
    icon: MegaphoneSimpleIcon,
    keywords: ['announcement', 'notice', 'broadcast', 'email', 'schedule', 'post'],
    whereToStart: 'Announcements',
    estimatedMinutes: 3,
    steps: [
      {
        title: 'Open Announcements',
        detail: 'Click Announcements in the left sidebar.',
      },
      {
        title: 'Click "New Announcement"',
        detail: 'The "Create Announcement" dialog opens.',
      },
      {
        title: 'Write the announcement',
        detail:
          'Enter a Subject, choose a Type (General, Maintenance, or Emergency), and write your Message. Attach files if needed.',
      },
      {
        title: 'Choose delivery settings',
        detail:
          'Toggle "Board Members Only" to restrict the audience, "Send Email" to also email members, and "Schedule for Later" to pick a future date and time.',
        tip: 'Members always receive the notification inside the app — "Send Email" just adds an email copy.',
      },
      {
        title: 'Send or schedule',
        detail:
          'Click "Send Now" (or "Schedule For Later"). You can also "Save Draft" to finish it later.',
      },
    ],
    related: ['upload-a-document'],
  },
  {
    slug: 'submit-a-request',
    title: 'Submit a request',
    summary: 'Report a maintenance issue or send a question to management.',
    categoryId: 'communication',
    audience: 'everyone',
    icon: PaperPlaneTiltIcon,
    keywords: ['request', 'maintenance', 'issue', 'ticket', 'architectural', 'question'],
    whereToStart: 'Requests',
    estimatedMinutes: 2,
    steps: [
      {
        title: 'Open Requests',
        detail: 'Click Requests in the left sidebar.',
      },
      {
        title: 'Create a new request',
        detail: 'Start a new request and choose the category that best fits your issue.',
      },
      {
        title: 'Describe the issue',
        detail: 'Explain what you need and attach photos or files so management has the full picture.',
      },
      {
        title: 'Track and reply',
        detail:
          'Submit the request, then follow its status and use the conversation area to add updates or answer questions.',
      },
    ],
    related: ['request-statuses'],
  },
  {
    slug: 'request-statuses',
    title: 'How request statuses work',
    summary: 'Understand what each status means, who can change it, and how requests move through the workflow.',
    categoryId: 'communication',
    audience: 'everyone',
    icon: TagIcon,
    keywords: [
      'request status',
      'status',
      'workflow',
      'new',
      'initial review',
      'quoting',
      'quoted',
      'approved',
      'rejected',
      'scheduled',
      'completed',
      'quality review',
      'maintenance',
      'general',
      'incident',
      'security',
      'suggestion',
      'arc',
      'state',
      'progress',
    ],
    whereToStart: 'Requests',
    steps: [
      {
        title: 'New',
        detail:
          'Every request starts here the moment a resident submits it. Management can see it and will move it forward once they begin reviewing it.',
      },
      {
        title: 'Initial Review',
        detail:
          'A manager has opened the request and is assessing it. For maintenance requests this may involve deciding whether a quote is needed. For general, incident, security, and suggestion requests a manager may resolve it directly from here.',
      },
      {
        title: 'Quoting (maintenance only)',
        detail:
          'Management is gathering quotes from vendors or contractors. The request may move back and forth between Quoting and Quoted while quotes are compared.',
      },
      {
        title: 'Quoted (maintenance only)',
        detail:
          'At least one quote has been received and is under final consideration before approval.',
      },
      {
        title: 'Final Review (maintenance only)',
        detail:
          'Management is doing a last check before approving the work. They may send it back to Quoting if the quotes need revision.',
      },
      {
        title: 'Approved',
        detail:
          'The request has been formally approved. For maintenance requests this means the work is authorized and will be scheduled. You will receive a notification when this happens.',
      },
      {
        title: 'Scheduled (maintenance only)',
        detail:
          'The work has been booked with a vendor and is upcoming. Once completed it moves to Quality Review.',
      },
      {
        title: 'Quality Review (maintenance only)',
        detail:
          'The work has been done and management is verifying it meets expectations. If issues are found it may go back to Scheduled.',
      },
      {
        title: 'Completed',
        detail:
          'The request is fully resolved. No further action is needed. You will receive a notification when your request is marked complete.',
        tip: 'Completed is a terminal status — the request cannot be re-opened. Submit a new request if the issue recurs.',
      },
      {
        title: 'Rejected',
        detail:
          'Management has declined the request. Rejection is also terminal. The manager may include a reason in the conversation thread.',
        tip: 'Not sure why your request was rejected? Use the conversation area to ask for clarification.',
      },
    ],
    related: ['submit-a-request', 'arc-request-statuses'],
  },
  {
    slug: 'arc-request-statuses',
    title: 'ARC review request statuses',
    summary: 'Understand the Architectural Review Committee workflow and what each status means for home improvement requests.',
    categoryId: 'communication',
    audience: 'everyone',
    icon: TagIcon,
    keywords: [
      'arc',
      'architectural review',
      'committee',
      'home improvement',
      'modification',
      'arc submitted',
      'under review',
      'approved with conditions',
      'needs more info',
      'approved',
      'rejected',
    ],
    whereToStart: 'Requests → ARC Review',
    steps: [
      {
        title: 'New',
        detail:
          'Your ARC request has been submitted and is waiting for the committee to pick it up.',
      },
      {
        title: 'ARC Submitted',
        detail:
          'The committee has acknowledged the request and is preparing to review it.',
      },
      {
        title: 'ARC Under Review',
        detail:
          'The Architectural Review Committee is actively evaluating your request. They may ask for more information before making a decision.',
      },
      {
        title: 'Needs More Information',
        detail:
          'The committee requires additional details, photos, or documents before they can continue. Check the conversation thread for what is needed and reply with the information. The request will move back to Under Review once you respond.',
        tip: 'Respond promptly — the committee cannot proceed until they have what they asked for.',
      },
      {
        title: 'Approved',
        detail:
          'The ARC has fully approved your modification request. You may proceed with the work as described in your submission.',
      },
      {
        title: 'Approved with Conditions',
        detail:
          'The ARC has approved the request but with specific requirements or restrictions. Read the committee\'s notes carefully — you must follow the stated conditions for the approval to remain valid.',
      },
      {
        title: 'Rejected',
        detail:
          'The committee has declined the request. The reason is typically included in the conversation thread. You may submit a new request if you revise the plans to address the committee\'s concerns.',
      },
    ],
    related: ['submit-a-request', 'request-statuses'],
  },
  {
    slug: 'upload-a-document',
    title: 'Upload a document',
    summary: 'Share rules, forms, and files with your community.',
    categoryId: 'documents-meetings',
    audience: 'community-manager',
    icon: FileArrowUpIcon,
    keywords: ['document', 'file', 'upload', 'forms', 'rules', 'folder', 'private', 'public'],
    whereToStart: 'Documents',
    estimatedMinutes: 2,
    steps: [
      {
        title: 'Open Documents',
        detail: 'Click Documents in the left sidebar. Open or create a folder if you want to organize the file.',
      },
      {
        title: 'Click "Upload"',
        detail: 'The "Upload Files" dialog opens. (Use "New Folder" first if you need a place to put it.)',
      },
      {
        title: 'Choose your files',
        detail: 'Drag and drop files or click to browse. Most file types are supported.',
      },
      {
        title: 'Set visibility',
        detail: 'Choose Public (visible to members) or Private (managers only) for the upload.',
      },
      {
        title: 'Upload',
        detail: 'Click "Upload Files". A confirmation shows how many files were uploaded.',
      },
    ],
    related: ['find-documents'],
  },
  {
    slug: 'find-documents',
    title: 'Find a document',
    summary: 'Locate forms, rules, and files shared by your association.',
    categoryId: 'documents-meetings',
    audience: 'everyone',
    icon: FileTextIcon,
    keywords: ['documents', 'find', 'search', 'forms', 'rules', 'download'],
    whereToStart: 'Documents',
    estimatedMinutes: 1,
    steps: [
      {
        title: 'Open Documents',
        detail: 'Click Documents in the left sidebar.',
      },
      {
        title: 'Browse or search',
        detail: 'Open folders to browse, or search for the file you need by name.',
      },
      {
        title: 'Open the document',
        detail: 'Click a document to preview it, download it, or review its details.',
      },
    ],
  },
  {
    slug: 'schedule-a-meeting',
    title: 'Schedule a meeting',
    summary: 'Create a board meeting with an agenda and supporting documents.',
    categoryId: 'documents-meetings',
    audience: 'board',
    icon: CalendarPlusIcon,
    keywords: ['meeting', 'board', 'agenda', 'schedule', 'minutes'],
    whereToStart: 'Meetings',
    estimatedMinutes: 3,
    steps: [
      {
        title: 'Open Meetings',
        detail: 'Click Meetings in the left sidebar.',
      },
      {
        title: 'Click "New Meeting"',
        detail: 'The "Create Meeting" dialog opens.',
        tip: 'If you don\'t see "New Meeting", your role may not allow scheduling. Board members and account admins can create meetings.',
      },
      {
        title: 'Set the type, date, and time',
        detail: 'Choose a Meeting Type, then set the Meeting Date and Meeting Time. Meetings must be scheduled at least an hour ahead.',
      },
      {
        title: 'Add the agenda',
        detail: 'Write the Meeting Agenda and attach a meeting document if you have one.',
      },
      {
        title: 'Create',
        detail: 'Click "Create". You\'ll land on the new meeting\'s page.',
      },
    ],
  },
  {
    slug: 'connect-stripe',
    title: 'Connect Stripe for payments',
    summary: 'Link a Stripe account so your community can collect deposits and fees.',
    categoryId: 'settings-payments',
    audience: 'community-manager',
    icon: BankIcon,
    keywords: ['stripe', 'payments', 'payouts', 'connect', 'deposits', 'fees', 'bank'],
    whereToStart: 'Settings → Community Settings → Payments',
    estimatedMinutes: 5,
    steps: [
      {
        title: 'Open the Payments settings',
        detail: 'Go to Settings → Community Settings, click Payments, then the "Stripe Setup" tab.',
        tip: 'If you see a notice that payments aren\'t enabled, your account administrator must enable them first.',
      },
      {
        title: 'Start the connection',
        detail: 'Under "Stripe Connect", click "Connect Stripe account". You\'ll be redirected to Stripe.',
      },
      {
        title: 'Complete Stripe onboarding',
        detail:
          'Sign in to Stripe (or create an account) and provide the business and bank details Stripe asks for. When finished you\'ll be sent back to HOAONE.',
      },
      {
        title: 'Confirm it\'s active',
        detail:
          'Back in HOAONE you\'ll see a "Connected & active" status. You can then toggle "Deposits enabled for residents".',
        tip: 'Use "Open Stripe Dashboard" to view payouts, or "Re-authorize"/"Disconnect" to manage the connection later.',
      },
    ],
    related: ['set-up-an-amenity', 'create-a-reservation'],
  },
  {
    slug: 'set-up-an-amenity',
    title: 'Set up an amenity (availability, pricing & rules)',
    summary: 'Create a bookable amenity and configure its hours, fees, deposit, and reservation rules.',
    categoryId: 'reservations',
    audience: 'community-manager',
    icon: SlidersIcon,
    keywords: [
      'amenity',
      'setup',
      'configure',
      'clubhouse',
      'pool',
      'pricing',
      'fee',
      'deposit',
      'availability',
      'hours',
      'time block',
      'rules',
      'guests',
    ],
    whereToStart: 'Settings → Community Settings → Amenities',
    estimatedMinutes: 6,
    steps: [
      {
        title: 'Open the Amenities settings',
        detail: 'Go to Settings → Community Settings, then click Amenities in the sub-navigation.',
        tip: "Don't see Community Settings? You need a Community Manager (or account admin) role.",
      },
      {
        title: 'Add a new amenity',
        detail: 'Click "Add Amenity". The form is split into tabs: Overview, Availability, and Fees & Rules.',
      },
      {
        title: 'Overview — name, description, and rules',
        detail:
          'On the Overview tab, enter the Amenity Name (e.g. Clubhouse, Pool) and a Description residents see when booking. Add any Rules & Policies (noise limits, cleanup, etc.).',
      },
      {
        title: 'Availability — set the bookable hours',
        detail:
          'On the Availability tab, toggle each day on and set open/close times. Optionally enable Seasonal Availability to limit bookings to certain months.',
        tip: 'Use "Fixed Time Block" to lock every reservation to the same length (e.g. 6-hour slots), or leave it blank and set Minimum/Maximum Duration for flexible booking.',
      },
      {
        title: 'Fees & Rules — pricing and deposit',
        detail:
          'On the Fees & Rules tab, set the Total Fee (full reservation cost) and a Deposit Amount collected upfront at booking. The remaining balance is calculated automatically. Add Fee Notes (e.g. "refundable if no damage"). Leave blank for a free amenity.',
        tip: 'Deposits and fees are only charged once Stripe is connected — see "Connect Stripe for payments". Until then, amounts are saved but not collected.',
      },
      {
        title: 'Set the guest policy and required documents',
        detail:
          'Still on Fees & Rules, optionally set Max Guests, require a guest count, or ask whether alcohol will be served. Add any Required Documents (e.g. Photo ID, signed agreement) residents must upload.',
      },
      {
        title: 'Choose approval and active status',
        detail:
          'Turn on "Requires Approval" if a manager must approve each booking. Keep "Active" on to make the amenity visible to residents (turn it off to hide it while you finish setup).',
      },
      {
        title: 'Save the amenity',
        detail: 'Click Create. The amenity appears in the Amenities list and, if active, becomes bookable on the Reservations page.',
      },
    ],
    related: ['connect-stripe', 'create-a-reservation', 'approve-a-reservation'],
  },
  {
    slug: 'configure-community-preferences',
    title: 'Configure community settings & preferences',
    summary: 'Set your community details, choose which pages members see, and add resident profile fields.',
    categoryId: 'settings-payments',
    audience: 'community-manager',
    icon: GearSixIcon,
    keywords: [
      'settings',
      'preferences',
      'community',
      'configure',
      'discussion',
      'pages',
      'custom field',
      'profile',
      'details',
      'reservation description',
    ],
    whereToStart: 'Settings → Community Settings',
    estimatedMinutes: 4,
    steps: [
      {
        title: 'Open Community Settings',
        detail: 'Go to Settings → Community Settings. Start on the Details tab to review your community name, address, and contact info.',
      },
      {
        title: 'Open Preferences',
        detail: 'Click Preferences in the sub-navigation to configure how the community appears to members.',
      },
      {
        title: 'Customize the reservation page text',
        detail:
          'Edit the "Reservation page description" to change the message shown at the top of the amenity reservations page, then click Save. Leave it blank to use the default.',
      },
      {
        title: 'Choose which pages members see',
        detail:
          'Under "Pages", toggle features like Discussion on or off to control what appears in the member sidebar, then click Save.',
      },
      {
        title: 'Add resident profile fields (optional)',
        detail:
          'Under "Resident profile fields", add custom fields (e.g. Emergency contact, Parking spot). Use "Visible to member" and "Editable by member" to control whether residents can see and update each field.',
      },
    ],
    related: ['add-a-user', 'set-up-an-amenity'],
  },
  {
    slug: 'invite-an-administrator',
    title: 'Invite an account administrator',
    summary: 'Give a colleague account-level admin access across your communities.',
    categoryId: 'settings-payments',
    audience: 'account',
    icon: ShieldCheckIcon,
    keywords: ['administrator', 'admin', 'invite', 'account', 'access', 'staff', 'team', 'permissions', 'system users'],
    whereToStart: 'Settings → Account Settings → Administrators',
    estimatedMinutes: 3,
    steps: [
      {
        title: 'Open Account Settings',
        detail: 'Go to Settings → Account Settings, then click Administrators in the sub-navigation.',
        tip: 'Account Settings is only available to account owners, admins, and account managers.',
      },
      {
        title: 'Click "Invite Administrator"',
        detail: 'The button is in the top-right of the Administrators page. An invite dialog opens.',
      },
      {
        title: 'Enter their details and role',
        detail: 'Fill in the person\'s name and email, then choose the account role that matches the access they need.',
      },
      {
        title: 'Send the invitation',
        detail:
          'Submit the form. The person receives an email invitation to set their password and sign in. They\'ll appear in the Administrators list as pending until they accept.',
        tip: 'Need to send it again? Use "Resend Invitation" next to a pending administrator.',
      },
    ],
    related: ['create-a-community', 'add-a-user'],
  },
  {
    slug: 'create-a-community',
    title: 'Create a community',
    summary: 'Add a new HOA or association to your account and fill in its details.',
    categoryId: 'settings-payments',
    audience: 'account',
    icon: BuildingsIcon,
    keywords: ['community', 'create', 'add', 'association', 'hoa', 'new community', 'account', 'setup'],
    whereToStart: 'Settings → Account Settings → Communities',
    estimatedMinutes: 3,
    steps: [
      {
        title: 'Open the Communities list',
        detail: 'Go to Settings → Account Settings, then click Communities in the sub-navigation.',
      },
      {
        title: 'Click "New Community"',
        detail: 'A dialog opens asking for the community name.',
      },
      {
        title: 'Name the community',
        detail: 'Enter the community name and click Create. You\'ll be taken to the new community\'s detail page.',
      },
      {
        title: 'Fill in the details',
        detail:
          'On the community page, add the address and contact details. From here you can continue setup: add units, invite users, set up amenities, and connect Stripe.',
      },
    ],
    related: ['invite-an-administrator', 'add-a-unit', 'set-up-an-amenity'],
  },
  {
    slug: 'view-and-restore-deleted-users',
    title: 'View and restore a deleted user',
    summary: 'Find users who have deleted their account and restore their access if needed.',
    categoryId: 'residents-units',
    audience: 'community-manager',
    icon: UserMinusIcon,
    keywords: [
      'deleted user',
      'restore',
      'recover',
      'account deletion',
      'archived',
      'removed',
      'reactivate',
      'soft delete',
    ],
    whereToStart: 'Management → Users',
    estimatedMinutes: 2,
    steps: [
      {
        title: 'Open the Users page',
        detail: 'In the left sidebar, under Management, click Users.',
      },
      {
        title: 'Show deleted users',
        detail:
          'At the top of the Users list, toggle "Show deleted users" (or use the filter menu). Deleted users appear greyed out with a "Deleted" badge.',
        tip: 'When a resident deletes their own account from the mobile app, their login is removed but their community records — requests, reservations, history — are preserved.',
      },
      {
        title: 'Open the user',
        detail: 'Click the user\'s row to open their detail page.',
      },
      {
        title: 'Restore the account',
        detail:
          'Click "Restore User". Their login is reactivated and they can sign in again. If they need a new password, use "Send password reset" after restoring.',
        tip: 'If the resident originally deleted their account and wants back in, they should contact you directly — they cannot restore themselves from the app.',
      },
    ],
    related: ['add-a-user', 'delete-your-own-account'],
  },
  {
    slug: 'delete-your-own-account',
    title: 'Delete your account',
    summary: 'Permanently remove your HOAONE login and personal information from the app.',
    categoryId: 'residents-units',
    audience: 'everyone',
    icon: TrashIcon,
    keywords: [
      'delete account',
      'remove account',
      'cancel account',
      'close account',
      'privacy',
      'personal data',
      'gdpr',
    ],
    whereToStart: 'More → Delete Account',
    estimatedMinutes: 2,
    steps: [
      {
        title: 'Open the More menu',
        detail: 'In the mobile app, tap the More tab in the bottom navigation bar.',
      },
      {
        title: 'Tap "Delete Account"',
        detail: 'Scroll to the Legal section and tap Delete Account.',
      },
      {
        title: 'Enter your password',
        detail: 'Type your current account password to confirm it\'s you.',
      },
      {
        title: 'Type DELETE to confirm',
        detail: 'In the confirmation field, type DELETE (all caps) to acknowledge this is permanent.',
      },
      {
        title: 'Confirm',
        detail:
          'Tap "Delete My Account" and confirm the final prompt. Your login is removed immediately and you are signed out.',
        tip: 'Your community history (requests, reservations, etc.) is kept for record-keeping purposes but is no longer linked to your name. If you change your mind, contact your community manager — a manager can restore your account.',
      },
    ],
    related: ['view-and-restore-deleted-users'],
  },
];

export function getArticleBySlug(slug: string): HelpArticle | undefined {
  return HELP_ARTICLES.find((article) => article.slug === slug);
}
